import type { TaskHelpRequestEvidence, TaskHelpRequestNote } from '@erp/rpc';

export interface HelpRequestRunPath {
  to: '/agents/$agentDefinitionId/sessions/$agentRunId';
  params: { agentDefinitionId: string; agentRunId: string };
}

export interface HelpRequestFailedStepView {
  procedureName: string | null;
  stepIndex: number;
  stepName: string;
  error: string | null;
  failedAt: Date;
  /** The operator's newest note: usually its conclusion, quoting what blocked it. */
  latestNote: string | null;
  /** Older notes, oldest first, shown on demand. */
  earlierNotes: TaskHelpRequestNote[];
  screenshotUrl: string | null;
}

export interface HelpRequestEvidenceView {
  failedStep: HelpRequestFailedStepView | null;
  runPath: HelpRequestRunPath | null;
}

/** The run page for the escalating attempt; null without an agent to route under. */
function runPath(evidence: TaskHelpRequestEvidence): HelpRequestRunPath | null {
  if (!evidence.agentDefinitionId) return null;
  return {
    to: '/agents/$agentDefinitionId/sessions/$agentRunId',
    params: {
      agentDefinitionId: evidence.agentDefinitionId,
      agentRunId: evidence.sessionRunId,
    },
  };
}

function failedStepView(
  step: NonNullable<TaskHelpRequestEvidence['failedStep']>,
): HelpRequestFailedStepView {
  const { notes, ...rest } = step;
  return {
    ...rest,
    latestNote: notes.at(-1)?.text ?? null,
    earlierNotes: notes.slice(0, -1),
  };
}

/**
 * What the Needs Input callout shows about the escalating attempt. Null when
 * there is nothing to show, so the callout looks exactly as it did before.
 */
export function helpRequestEvidenceView(
  evidence: TaskHelpRequestEvidence | null | undefined,
): HelpRequestEvidenceView | null {
  if (!evidence) return null;
  const view = {
    failedStep: evidence.failedStep
      ? failedStepView(evidence.failedStep)
      : null,
    runPath: runPath(evidence),
  };
  return view.failedStep || view.runPath ? view : null;
}
